<template>
  <busy-area :busy="loadingCampaignSurplusSummary">
    <h2 style="padding-top: 10px">
      {{ $t('surplus.surplusGroup') }}
    </h2>
    <div class="flex flex-wrap gap-x-10 gap-y-10 pb-10 pt-5">
      <div class="flex flex-col justify-stretch">
        <table class="statisticsTable h-full">
          <thead>
            <tr>
              <th>{{ $t('surplus.computedWeek') }}</th>
              <th>{{ $t('surplus.totalDrivers') }}</th>
              <th>{{ $t('surplus.driversWithFollowUpCampaign') }}</th>
              <th>{{ $t('surplus.driversWithoutFollowUpCampaign') }}</th>
              <th>{{ $t('surplus.noFollowUpPercentage') }}</th>
              <th>{{ $t('surplus.driversWithoutFollowUpCampaignApproximated') }}</th>
              <th>{{ $t('surplus.noFollowUpPercentageApproximated') }}</th>
              <th>{{ $t('surplus.driversWithoutFollowUpAndNonCampaignTour') }}</th>
              <th>{{ $t('surplus.noFollowUpWithTourPercentage') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(entry, week) in weeklyEntries" :key="week">
              <td>{{ week }}</td>
              <td>{{ entry?.totalDrivers }}</td>
              <td>{{ entry?.driversWithFollowUpCampaign }}</td>
              <td>{{ entry?.driversWithoutFollowUpCampaign }}</td>
              <td>{{ Math.round(entry?.noFollowUpPercentage ?? 0) }}%</td>
              <td>{{ entry?.driversWithoutFollowUpCampaignApproximated }}</td>
              <td>{{ Math.round(entry?.noFollowUpPercentageApproximated ?? 0) }}%</td>
              <td>{{ entry?.driversWithoutFollowUpAndNonCampaignTour }}</td>
              <td>{{ Math.round(entry?.noFollowUpWithTourPercentage ?? 0) }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </busy-area>
</template>

<script lang="ts" setup>
import BusyArea from '@utils/busy/BusyArea.vue';
import { useI18n } from 'vue-i18n';
import { useProvider } from '@utils/table/hooks/provider';
import { computed } from 'vue';
import type { CampaignGroup } from '@/entities/Entities';

export interface CampaignSurplusSummaryEntryDto {
  totalDrivers: number | null;
  driversWithFollowUpCampaign: number | null;
  driversWithoutFollowUpCampaign: number | null;
  driversWithoutFollowUpCampaignApproximated: number | null;
  driversWithoutFollowUpAndNonCampaignTour: number | null;
  noFollowUpPercentage: number | null;
  noFollowUpWithTourPercentage: number | null;
  noFollowUpPercentageApproximated: number | null;
}

export interface CampaignSurplusSummaryDto {
  campaignGroup: CampaignGroup;
  entryWeekOne: CampaignSurplusSummaryEntryDto | null;
  entryWeekTwo: CampaignSurplusSummaryEntryDto | null;
  entryWeekThree: CampaignSurplusSummaryEntryDto | null;
  entryWeekFour: CampaignSurplusSummaryEntryDto | null;
  computedWeek?: number | null;
  entries?: CampaignSurplusSummaryEntryDto[] | null;
}

interface Props {
  providerParams: object;
}

const { t } = useI18n();

const props = defineProps<Props>();

const {
  data: campaignSurplusSummaryItem,
  loading: loadingCampaignSurplusSummary,
} = useProvider<CampaignSurplusSummaryDto>('/campaign_surplus/summary', props.providerParams);

const weeklyEntries = computed(() => ({
  1: campaignSurplusSummaryItem?.value?.entryWeekOne,
  2: campaignSurplusSummaryItem?.value?.entryWeekTwo,
  3: campaignSurplusSummaryItem?.value?.entryWeekThree,
  4: campaignSurplusSummaryItem?.value?.entryWeekFour,
}));

</script>

<style lang="scss" scoped>
.statisticsTable {
  @apply border;

  tr {
    td, th {
      &:not(:last-child) {
        @apply border-r;
      }

      @apply px-5 py-2;
      text-align: right;
    }

    th {
      @apply border-b;
    }
  }
}
</style>
